import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, MapPin } from "lucide-react";
import { api, euro } from "@/lib/api";
import { MondialRelayPicker } from "./MondialRelayPicker";
import { useLang, SHIP_EN } from "@/lib/i18n";

export const RELAY_METHODS = ["mondial_relay", "mondial_relay_eu"];
const HINTS = {
  colissimo: "12,90 € en dessous de 16 € d'achat · 19,99 € dès 16 €",
  colissimo_domtom: "Guadeloupe, Martinique, Guyane, La Réunion, Mayotte… · 11,90 € jusqu'à 30 € d'achat, 14,99 € au-delà",
  mondial_relay_eu: "Belgique, Luxembourg, Pays-Bas, Espagne, Portugal, Allemagne, Autriche, Italie, Pologne",
  colissimo_world: "États-Unis, Canada, Afrique, Irlande, pays nordiques… · 12,90 € jusqu'à 48,99 €, 19,90 € jusqu'à 118,99 €, offerte dès 119 €",
  colissimo_uk_ch: "Royaume-Uni, Suisse · 9,90 €, offerte dès 79 € · droits ou taxes locaux possibles à la livraison",
};

/** Modes de livraison ; le montant dépend du sous-total du panier (paliers Colissimo). */
export function useShippingMethods(subtotal = 0) {
  const s = Math.round((subtotal || 0) * 100) / 100;
  return useQuery({
    queryKey: ["shipping", s],
    queryFn: async () => {
      const r = await api.get("/shipping/methods", { params: { subtotal: s } });
      return Array.isArray(r.data) ? r.data : [];
    },
    placeholderData: (prev) => prev,
  });
}

export function ShippingSelector({ method, setMethod, relay, setRelay, subtotal = 0, methods: fixed }) {
  const { data } = useShippingMethods(subtotal);
  const methods = Array.isArray(fixed) ? fixed : Array.isArray(data) ? data : [];
  const [pick, setPick] = useState(false);
  const { t, lang } = useLang();
  const en = lang === "en";
  const current = methods.find((m) => m.id === method);
  const choose = (m) => {
    const zone = m.id === "mondial_relay" ? ["FR"] : m.countries || [];
    if (relay && RELAY_METHODS.includes(m.id) && !zone.includes(relay.country || "FR")) setRelay(null);
    setMethod(m.id);
  };

  return (
    <div data-testid="shipping-selector" className="space-y-2">
      <p className="eyebrow mb-3">{t("Livraison")}</p>
      {methods.map((m) => {
        const on = method === m.id;
        const hint = en ? SHIP_EN[m.id]?.hint : HINTS[m.id];
        return (
          <div key={m.id}>
            <button data-testid={`shipping-option-${m.id}`} onClick={() => choose(m)} className={`flex w-full items-center gap-3 rounded-sm border px-4 py-3 text-left transition-colors ${on ? "border-[var(--gold)] bg-[rgba(217,180,106,0.08)]" : "border-[var(--line)] hover:border-[var(--gold)]/50"}`}>
              <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${on ? "border-[var(--gold)] bg-[var(--gold)]" : "border-[var(--muted)]"}`}>{on && <Check className="h-3 w-3 text-[var(--ink)]" />}</span>
              <span className="flex-1">
                <span className="block text-sm">{(en && SHIP_EN[m.id]?.label) || m.label}</span>
                <span className="text-xs text-[var(--parchment)]/70">{t(m.delay)}{!fixed && hint ? ` · ${hint}` : ""}</span>
              </span>
              <span data-testid={`shipping-price-${m.id}`} className="shrink-0 text-sm text-[var(--gold-soft)]">{m.amount ? euro(m.amount) : t("Gratuit")}</span>
            </button>
            {on && RELAY_METHODS.includes(m.id) && (
              <button data-testid="shipping-choose-relay" onClick={() => setPick(true)} className="mt-2 flex w-full items-center gap-2 rounded-sm border border-dashed border-[var(--gold)]/60 px-4 py-3 text-left text-xs text-[var(--parchment)]/90 hover:bg-[rgba(217,180,106,0.06)]">
                <MapPin className="h-4 w-4 shrink-0 text-[var(--gold)]" />
                {relay ? (
                  <span data-testid="shipping-relay-selected"><b className="text-[var(--gold-soft)]">{t(relay.kind === "locker" ? "Locker" : "Point Relais")} · {relay.name}</b><br />{relay.address}, {relay.zip} {relay.city}{relay.country && relay.country !== "FR" ? ` (${relay.country})` : ""} — <u>{t("modifier")}</u></span>
                ) : (
                  <span>{t("Choisir mon Point Relais ou Locker")}</span>
                )}
              </button>
            )}
          </div>
        );
      })}
      <MondialRelayPicker open={pick} onOpenChange={setPick} onChoose={setRelay} countries={current?.id === "mondial_relay_eu" ? current.countries : null} />
    </div>
  );
}
